import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as CloseButton } from '../../assets/icons/close-box.svg';
import { ReactComponent as Calender } from '../../assets/icons/timer-icon.svg';
import { timeSlotValidation } from '../../shared-functions/Validation';
import MyButton from '../button/Button';
import CustomToggleButton from '../custom-toggle-button/CustomToggleButton';

interface CustomDateRangeProperties {
  inputLabel?: string;
  value: string[];
  name: string;
  placeholder?: string;
  onChangeValue: (name: string, value: string[]) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
}

/**
 * Custom the Date Picker function
 * @param {any} properties properties
 * @param {any} properties.inputLabel - The inputLabel prop
 * @param {any} properties.name - The name prop
 * @param {any} properties.value - The value prop
 * @param {any} properties.placeholder - The placeholder prop
 * @param {any} properties.onChangeValue - The onChangeValue prop
 * @param {any} properties.valueRequired - The valueRequired prop
 * @param {any} properties.readOnly - The readOnly prop
 * @returns {any} custom multi date picker
 */
function CustomMultiDatePicker({
  inputLabel,
  name,
  value,
  placeholder,
  onChangeValue,
  valueRequired,
  readOnly,
}: CustomDateRangeProperties) {
  const reference = useRef<HTMLDivElement>(null);
  const [allTimes, setAllTimes] = useState<string[]>([]);
  const [style, setStyle] = useState<any>({});
  const [openTab, setOpenTab] = useState<number>(0);
  const [customInput, setCustomInput] = useState<string>('');
  const [disable, setDisable] = useState<boolean>(true);
  const [selectedTimes, setSelectedTimes] = useState<string[]>(value);

  const [open, setOpen] = useState<boolean>(false);
  /**
   * Handle the Value change
   * @param {any} changeValue change value
   */
  const handleValueChange = (changeValue: string) => {
    if (selectedTimes.includes(changeValue)) {
      setSelectedTimes(selectedTimes.filter((date) => date !== changeValue));
    } else {
      setSelectedTimes([...selectedTimes, changeValue]);
    }
  };

  /**
   * Handle the Value change
   */
  const handleSubmitDetails = () => {
    onChangeValue(name, [...selectedTimes]);
    setOpen(false);
  };

  /**
   * Handle the add custom time slot function
   */
  const addCustomTimeSlot = () => {
    onChangeValue(name, [...selectedTimes, customInput]);
    setSelectedTimes([...selectedTimes, customInput]);
    setOpen(false);
    setDisable(false);
    setCustomInput('');
  };

  /**
   * Handle the Input click
   * @param {any} event event
   */
  const openCalenderView = (event: any) => {
    if (!readOnly) {
      setOpen(true);
      let rect;
      if (event.target.className === 'custom-input-container') {
        rect = event.target.getBoundingClientRect();
      } else {
        rect = event.target.closest('.custom-input-container').getBoundingClientRect();
      }
      if (window.innerHeight > rect.top + rect.height + 285) {
        setStyle({
          top: rect.top + rect.height,
          left: rect.left,
          width: rect.width,
        });
      } else {
        setStyle({
          top: rect.top - 275 - rect.height,
          left: rect.left,
          width: rect.width,
        });
      }
    }
  };

  /**
   * Handle the page change function
   * @param {any} number page number
   */
  const changePageHandle = (number: number) => {
    setOpenTab(number);
    let times = [];
    if (number === 0) {
      times = [
        '12:00 AM - 01:00 AM',
        '01:00 AM - 02:00 AM',
        '02:00 AM - 03:00 AM',
        '03:00 AM - 04:00 AM',
        '04:00 AM - 05:00 AM',
        '05:00 AM - 06:00 AM',
        '06:00 AM - 07:00 AM',
        '07:00 AM - 08:00 AM',
        '08:00 AM - 09:00 AM',
        '09:00 AM - 10:00 AM',
        '10:00 AM - 11:00 AM',
        '11:00 AM - 12:00 PM',
      ];
    } else {
      times = [
        '12:00 PM - 01:00 PM',
        '01:00 PM - 02:00 PM',
        '02:00 PM - 03:00 PM',
        '03:00 PM - 04:00 PM',
        '04:00 PM - 05:00 PM',
        '05:00 PM - 06:00 PM',
        '06:00 PM - 07:00 PM',
        '07:00 PM - 08:00 PM',
        '08:00 PM - 09:00 PM',
        '09:00 PM - 10:00 PM',
        '10:00 PM - 11:00 PM',
        '11:00 PM - 12:00 AM',
      ];
    }
    setAllTimes(times);
  };

  /**
   * Handle close the calender over flow click function
   * @param {any} event mouse event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };

  /**
   * Handle the custom input box function
   * @param {any} event input event
   */
  const handleCustomInput = (event: any) => {
    setCustomInput(event.target.value);
    setDisable(!timeSlotValidation(event.target.value));
  };

  /**
   * remove the date
   * @param {any} index index
   */
  const removeDate = (index: number) => {
    const oluValues = value;
    if (index > -1) {
      oluValues.splice(index, 1);
    }
    onChangeValue(name, [...oluValues]);
    setSelectedTimes([...oluValues]);
  };
  useEffect((): void => {
    changePageHandle(0);
    document.addEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="custom-input-date-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-date-input">
        <div className="custom-input-container" onClick={openCalenderView} role="presentation">
          {/* <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(event) => handleValueChange(event.target.value)}
            required={valueRequired}
            readOnly
          /> */}
          <div className="value-view">
            {value.length === 0 && <p>{placeholder}</p>}
            {value.map((date, index) => (
              <div className="item">
                {date}
                <div className="button" role="presentation" onClick={() => removeDate(index)}>
                  <CloseButton />
                </div>
              </div>
            ))}
          </div>

          <span>
            <Calender />
          </span>
        </div>
        {open &&
          ReactDOM.createPortal(
            <div className="time-session-box" ref={reference} style={style}>
              <div className="time-session-header">
                <div className="input-field">
                  <input
                    type="text"
                    className="input"
                    name="time_slot"
                    value={customInput}
                    placeholder="Add time slot (eg: 00:00 AM - 00:00 PM)"
                    onChange={handleCustomInput}
                  />
                  <button
                    type="button"
                    className="add-button"
                    onClick={addCustomTimeSlot}
                    disabled={disable}>
                    Add
                  </button>
                </div>
                <CustomToggleButton
                  labels={['Morning (AM)', 'Evening (PM)']}
                  onToggle={changePageHandle}
                  defaultActiveIndex={openTab}
                />
              </div>
              <div className="time-session-body">
                {allTimes.map((time) => (
                  <div
                    className={`time-session-item ${selectedTimes.includes(time) && 'active'}`}
                    onClick={() => handleValueChange(time)}
                    role="presentation">
                    {time}
                  </div>
                ))}
              </div>
              <div className="time-session-footer">
                <div className="button-group">
                  <MyButton label="Cancel" buttonType="back" onClickFunc={() => setOpen(false)} />
                  <MyButton label="Ok" buttonType="submit" onClickFunc={handleSubmitDetails} />
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

CustomMultiDatePicker.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  readOnly: false,
};

export default CustomMultiDatePicker;
